/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * CSS-only replacement for framer-motion's `motion.div` + `whileInView`.
 * Uses IntersectionObserver to trigger a CSS animation once the element
 * enters the viewport. ~0 KB JS vs ~30 KB for framer-motion.
 */

"use client";

import { useRef, useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface FadeInProps {
  children: ReactNode;
  className?: string;
  /** Delay in ms before animation starts after entering viewport */
  delay?: number;
  /** Margin around the root for triggering earlier/later */
  margin?: string;
  /** HTML tag to render */
  as?:
    | "div"
    | "section"
    | "header"
    | "article"
    | "li"
    | "dl"
    | "nav"
    | "ul"
    | "p"
    | "span"
    | "h1";
}

export function FadeIn({
  children,
  className,
  delay = 0,
  margin = "-80px",
  as: Tag = "div",
}: FadeInProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { rootMargin: margin },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [margin]);

  return (
    <Tag
      ref={ref as React.RefObject<never>}
      className={cn(
        "transition-all duration-500 ease-out",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5",
        className,
      )}
      style={
        delay > 0 && visible ? { transitionDelay: `${delay}ms` } : undefined
      }
    >
      {children}
    </Tag>
  );
}
